import { Component, ChangeDetectionStrategy, inject, input, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService, authErrorKey } from '../../services/auth.service';
import { BrandedLoaderComponent } from '../branded-loader/branded-loader.component';
import { AuthAlertComponent } from '../auth-alert/auth-alert.component';
import { MIN_PASSWORD_LENGTH, fieldError, passwordsMatchValidator } from '../../utils/password';

/** Form choosing a new password for the logged-in account (reset link or "My profile"). */
@Component({
  selector: 'app-new-password-form',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, BrandedLoaderComponent, AuthAlertComponent],
  templateUrl: './new-password-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPasswordFormComponent {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);

  /** Translation key of the submit button. */
  submitLabel = input('auth.reset.button');
  /** Emitted once the new password is saved. */
  changed = output<void>();

  readonly minPasswordLength = MIN_PASSWORD_LENGTH;
  readonly fieldError = fieldError;
  readonly email = this.authService.currentUser()?.email ?? '';

  form = this.fb.group({
    password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
    confirmPassword: ['', Validators.required],
  }, { validators: passwordsMatchValidator });

  errorMessage = signal<string | null>(null);
  isLoading = signal(false);

  async onSubmit() {
    if (this.isLoading()) return;
    this.form.markAllAsTouched();
    this.errorMessage.set(null);
    const { password } = this.form.getRawValue();
    if (this.form.invalid || !password) return;

    this.isLoading.set(true);
    try {
      const { error } = await this.authService.updatePassword(password);
      if (error) {
        this.errorMessage.set(authErrorKey(error));
      } else {
        this.form.reset();
        this.changed.emit();
      }
    } catch (err) {
      console.error('Unexpected password update error:', err);
      this.errorMessage.set('auth.error.unexpected');
    } finally {
      this.isLoading.set(false);
    }
  }
}
